import React, { createContext, useState, useCallback, useContext } from 'react';

interface InvalidLinksContextType {
  invalidLinks: string[];
  reportedTraceIds: Set<string>;
  addInvalidLink: (link: string) => void;
  clearInvalidLinks: () => void;
  hasReportedForTrace: (traceId: string) => boolean;
  markTraceAsReported: (traceId: string) => void;
  invalidLinksForPDF: string[];
  clearInvalidLinksForPDF: () => void;
  addInvalidLinkForPDF: (links: string[]) => void;
}

export const InvalidLinksContext = createContext<InvalidLinksContextType>({
  invalidLinks: [],
  reportedTraceIds: new Set<string>(),
  addInvalidLink: () => {},
  clearInvalidLinks: () => {},
  hasReportedForTrace: () => false,
  markTraceAsReported: () => {},
  invalidLinksForPDF: [],
  clearInvalidLinksForPDF: () => {},
  addInvalidLinkForPDF: () => {},
});

export const InvalidLinksProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [invalidLinks, setInvalidLinks] = useState<string[]>([]);
  const [reportedTraceIds, setReportedTraceIds] = useState<Set<string>>(new Set());
  const [invalidLinksForPDF, setInvalidLinksForPDF] = useState<string[]>([]);

  const addInvalidLink = useCallback((link: string) => {
    setInvalidLinks(prev => {
      if (!prev.includes(link)) {
        return [...prev, link];
      }
      return prev;
    });
  }, []);

  const clearInvalidLinks = useCallback(() => {
    setInvalidLinks([]);
  }, []);

  const hasReportedForTrace = useCallback((traceId: string) => {
    return reportedTraceIds.has(traceId);
  }, [reportedTraceIds]);

  const markTraceAsReported = useCallback((traceId: string) => {
    setReportedTraceIds(prev => {
      const newSet = new Set(prev);
      newSet.add(traceId);
      return newSet;
    });
  }, []);

  const addInvalidLinkForPDF = useCallback((links: string[]) => {
    const setFromLinks = new Set([...invalidLinksForPDF, ...links]);
    setInvalidLinksForPDF(Array.from(setFromLinks));
  }, []);

  const clearInvalidLinksForPDF = useCallback(() => {
    setInvalidLinksForPDF([]);
  }, []);


  return (
    <InvalidLinksContext.Provider value={{ 
      invalidLinks, 
      reportedTraceIds,
      addInvalidLink, 
      clearInvalidLinks,
      hasReportedForTrace,
      markTraceAsReported,
      addInvalidLinkForPDF,
      invalidLinksForPDF,
      clearInvalidLinksForPDF
    }}>
      {children}
    </InvalidLinksContext.Provider>
  );
};

export const useInvalidLinks = () => useContext(InvalidLinksContext); 